import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { MATERIAL_ICON_CART, MATERIAL_ICON_TRASH } from '../common/Button/Icon';
import { ChangeQuantityDialog } from '../common/ChangeQuantityDialog';
import { SelectionListDropDown } from '../common/SelectionListDropDown';
import LibraryManagedProductGroupDropdown from '../common/LibraryManagedProductGroupDropdown';
import { SelectionList } from '../../../models/SelectionList';
import { PriceTier } from '../../../models/PriceTier';
import { Button, ButtonSize, ButtonTheme } from '../common/Button';
import { ChangePriceDialog } from '../common/ChangePriceDialog';
import {
    BulkActionResponse,
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse
} from '../../store/api';
import { useEnvironment } from './EnvironmentContext';
import { ENVIRONMENT_DEMO } from '../../constants';
import { RenewalType } from '../../shop/renewals/utils/renewalTableTileTypes';
import { RequiredLoansSelector } from '../common/RequiredLoansSelector';
import { SubscriptionPeriodSelector } from '../common/SubscriptionPeriodSelector';
import { SubscriptionPeriodType } from '../../../models/Article';
import { useTranslation } from 'react-i18next';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../common/useListContentChecks';
import {
    AddToCartOptions,
    AddToListOptions,
    CopyToCartOptions,
    CopyToListOptions,
    isAddToListOptions,
    isCopyToCartOptions,
    isCopyToListOptions,
    isMoveOptions,
    MoveToListOptions
} from '../../../models/ListContentCheckOptions';
import {
    ACTION_ADD,
    ACTION_COPY,
    ACTION_DECREASE,
    ACTION_INCREASE,
    ACTION_MOVE,
    ACTION_SET
} from '../../store/constants';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';
import { UseMutationResult } from '@tanstack/react-query';
import { ErrorResponse } from '../notification/actions';
import { ConfirmOptionsModal, DEFAULT_SUBSCRIPTION_PERIOD_TYPE } from '../common/ConfirmOptionsModal';
import { useSelectionLists } from '../../api/selection-list/useSelectionLists';
import { useCreateNewSelectionList } from '../../api/selection-list/useCreateNewSelectionList';

const StSelectionBulkMenuContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 20px;

    padding: 10px 0;

    > * {
        margin: 0 3px;
    }
`;

const StCloseButton = styled.i`
    position: absolute;
    font-size: 16px;
    cursor: pointer;
    left: 0;
    line-height: 1 !important;
    margin: 0;
`;

type Props = {
    readonly selectedCount?: { readonly count?: number } | number;
    readonly isInSelectionMode?: boolean;
    readonly isMoveToListEnabled?: boolean;
    readonly isAddToCartEnabled?: boolean;
    readonly isAddToListEnabled?: boolean;
    readonly isCopyToListEnabled?: boolean;
    readonly isCopyToCartEnabled?: boolean;
    readonly isChangeQuantityEnabled?: boolean;
    readonly isDeleteFromEnabled?: boolean;
    readonly isAddToProductGroupEnabled?: boolean;
    readonly isChangeRequiredLoansEnabled?: boolean;
    readonly isChangeSubscriptionPeriodEnabled?: boolean;
    readonly deleteLabel?: string;
    readonly handleChangeRequiredLoan?: (type: RenewalType) => void;
    readonly bulkChangeSubscriptionPeriodMutation?: UseMutationResult<void, ErrorResponse, SubscriptionPeriodType>;
    readonly cancelSelection: () => void;
    readonly onBulkAddToCart?: (
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onBulkAddToList?: (
        list: SelectionList,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onBulkAddToGroup?: (productGroupId: number) => void;
    readonly onCopyToList?: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onBulkCopyToCart?: (performChecks: boolean) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onMoveToList?: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onCopyAmountChange?: (
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) => Promise<BulkActionResponse>;
    readonly onRemoveFrom?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
    readonly onChangePrice?: (priceTier: PriceTier, performChecks: boolean) => Promise<BulkActionResponse>;
    readonly hideListId?: string;
    readonly showSubscriptionStartDateOption?: boolean;
    readonly defaultPriceTier?: PriceTier;
    readonly isNothingSelected: boolean;
};

const SelectionBulkMenu: FC<Props> = ({
    selectedCount: selectedCountProp,
    isMoveToListEnabled = false,
    isAddToCartEnabled = true,
    isAddToListEnabled = false,
    isCopyToListEnabled = false,
    isCopyToCartEnabled = false,
    isChangeQuantityEnabled = false,
    isDeleteFromEnabled = false,
    isAddToProductGroupEnabled = false,
    isChangeRequiredLoansEnabled = false,
    isChangeSubscriptionPeriodEnabled = false,
    deleteLabel,
    cancelSelection,
    onBulkAddToCart,
    onBulkAddToList,
    onBulkAddToGroup = () => {},
    onCopyToList,
    onMoveToList,
    onCopyAmountChange,
    onRemoveFrom,
    onChangePrice,
    handleChangeRequiredLoan = () => {},
    bulkChangeSubscriptionPeriodMutation,
    hideListId,
    showSubscriptionStartDateOption = false,
    defaultPriceTier = { lendingModel: 'OCOU', level: 'MIN' },
    onBulkCopyToCart,
    isNothingSelected
}) => {
    const selectedCount = typeof selectedCountProp === 'number' ? selectedCountProp : selectedCountProp?.count;
    const { t } = useTranslation();
    // CatMan is not available in the demo environment
    const libraryManagedProductGroupsEnabled = useEnvironment() !== ENVIRONMENT_DEMO;

    const { mutate: createNewList } = useCreateNewSelectionList();

    const { selectionLists } = useSelectionLists();

    const [options, setOptions] = useState<AddToListOptions | AddToCartOptions | undefined>(undefined);
    const [addingToCart, setAddingToCart] = useState(false);

    const onBulkAdd = useCallback(
        (
            performChecks: boolean,
            options: CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
        ) => {
            if (isCopyToListOptions(options)) {
                if (!onCopyToList) {
                    throw new Error('technical error');
                }
                return onCopyToList(options.list, performChecks);
            }

            if (isCopyToCartOptions(options)) {
                if (!onBulkCopyToCart) {
                    throw new Error('technical error');
                }
                return onBulkCopyToCart(performChecks);
            }

            if (isMoveOptions(options)) {
                if (!onMoveToList || options.target !== ListActionTarget.List) {
                    throw new Error('technical error');
                }
                return onMoveToList(options.list, performChecks);
            }

            if (isAddToListOptions(options)) {
                if (!onBulkAddToList) {
                    throw new Error('technical error');
                }
                return onBulkAddToList(options.list, performChecks, options.priceTier, options.subscriptionPeriodType);
            } else {
                if (!onBulkAddToCart) {
                    throw new Error('technical error');
                }
                return onBulkAddToCart(performChecks, options.priceTier, options.subscriptionPeriodType);
            }
        },
        [onCopyToList, onBulkCopyToCart, onMoveToList, onBulkAddToList, onBulkAddToCart]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
    >({ onAction: onBulkAdd });

    const handleBulkAddToList = useCallback(
        async (list: SelectionList) => {
            setOptions({
                target: ListActionTarget.List,
                list,
                priceTier: defaultPriceTier,
                subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                action: ACTION_ADD
            });
        },
        [defaultPriceTier]
    );

    const handleBulkAddToCart = useCallback(async () => {
        setOptions({
            target: ListActionTarget.Cart,
            priceTier: defaultPriceTier,
            subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
            action: ACTION_ADD
        });
    }, [defaultPriceTier]);

    const handleBulkCopyToCart = useCallback(async () => {
        setAddingToCart(true);
        try {
            await changePrice({ target: ListActionTarget.Cart, action: ACTION_COPY });
        } finally {
            setAddingToCart(false);
        }
    }, [changePrice]);

    const handleBulkCopyToList = useCallback(
        async (list: SelectionList) => {
            await changePrice({
                target: ListActionTarget.List,
                list: list,
                action: ACTION_COPY
            });
        },
        [changePrice]
    );

    const handleBulkMoveToList = useCallback(
        async (list: SelectionList) => {
            await changePrice({ target: ListActionTarget.List, list: list, action: ACTION_MOVE });
        },
        [changePrice]
    );

    return (
        <StSelectionBulkMenuContainer data-displayname="SelectionBulkMenu">
            <StCloseButton className="material-icons" onClick={cancelSelection}>
                clear
            </StCloseButton>
            <div>
                <span>
                    {t('shop.subscriptions.selectionCount', {
                        selectionCount: selectedCount
                    })}
                </span>
            </div>
            {isAddToCartEnabled ? (
                <Button
                    text={t('addToCart')}
                    icon={MATERIAL_ICON_CART}
                    size={ButtonSize.SMALL}
                    theme={ButtonTheme.NEUTRAL}
                    onClick={handleBulkAddToCart}
                    disabled={addingToCart || isNothingSelected}
                    isLoading={addingToCart}
                />
            ) : null}
            {isCopyToCartEnabled ? (
                <Button
                    text={t('addToCart')}
                    icon={MATERIAL_ICON_CART}
                    size={ButtonSize.SMALL}
                    theme={ButtonTheme.NEUTRAL}
                    onClick={handleBulkCopyToCart}
                    disabled={addingToCart || isNothingSelected}
                    isLoading={addingToCart}
                />
            ) : null}
            {isCopyToListEnabled ? (
                <SelectionListDropDown
                    iconCode="copy-product"
                    label={t('copyToList')}
                    hideListId={hideListId}
                    lists={selectionLists}
                    onCreateNewListClicked={createNewList}
                    onListClick={handleBulkCopyToList}
                    disabled={isNothingSelected}
                />
            ) : null}
            {isMoveToListEnabled ? (
                <SelectionListDropDown
                    iconCode="move-product"
                    label={t('moveToList')}
                    hideListId={hideListId}
                    lists={selectionLists}
                    onCreateNewListClicked={createNewList}
                    onListClick={handleBulkMoveToList}
                    disabled={isNothingSelected}
                />
            ) : null}
            {libraryManagedProductGroupsEnabled && isAddToProductGroupEnabled ? (
                <LibraryManagedProductGroupDropdown
                    onListClick={onBulkAddToGroup}
                    isNothingSelected={isNothingSelected}
                />
            ) : null}
            {isAddToListEnabled ? (
                <SelectionListDropDown
                    label={t('addToList')}
                    lists={selectionLists}
                    onCreateNewListClicked={createNewList}
                    onListClick={handleBulkAddToList}
                    disabled={isNothingSelected}
                />
            ) : null}
            {onChangePrice ? (
                <ChangePriceDialog onChange={onChangePrice} isNothingSelected={isNothingSelected} />
            ) : null}
            {isChangeQuantityEnabled ? (
                <ChangeQuantityDialog
                    cancelProductSelection={cancelSelection}
                    onChange={(command, amount) =>
                        onCopyAmountChange ? onCopyAmountChange(amount, command) : Promise.resolve({})
                    }
                    label={t('changeQuantity')}
                    isNothingSelected={isNothingSelected}
                />
            ) : null}
            {isChangeSubscriptionPeriodEnabled && bulkChangeSubscriptionPeriodMutation ? (
                <SubscriptionPeriodSelector
                    bulkChangeSubscriptionPeriodMutation={bulkChangeSubscriptionPeriodMutation}
                    isNothingSelected={isNothingSelected}
                />
            ) : null}

            {isDeleteFromEnabled ? (
                <Button
                    text={deleteLabel ?? ''}
                    size={ButtonSize.SMALL}
                    theme={ButtonTheme.NEUTRAL}
                    icon={MATERIAL_ICON_TRASH}
                    onClick={event => onRemoveFrom?.(event)}
                    disabled={isNothingSelected}
                />
            ) : null}

            {isChangeRequiredLoansEnabled ? (
                <RequiredLoansSelector changeRequiredLoan={handleChangeRequiredLoan} />
            ) : null}
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
            <ConfirmOptionsModal
                options={options}
                changePrice={changePrice}
                showUsageConditionOption={true}
                showSubscriptionStartDateOption={showSubscriptionStartDateOption}
                closeModal={() => setOptions(undefined)}
            />
        </StSelectionBulkMenuContainer>
    );
};

export default SelectionBulkMenu;
